import { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Sparkles, ArrowLeft, ArrowRight, Loader2, CalendarHeart, Stethoscope, HeartPulse, Dumbbell, CalendarClock, CheckCircle2, ListChecks, Activity, Target, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

// Fallbacks — the backend /status config is the source of truth, but we keep a
// copy so onboarding still renders if the config field is ever missing.
const WORKOUT_BEFORE_FALLBACK = [
  { slug: 'yoga', label: 'Yoga' }, { slug: 'zumba', label: 'Zumba / Dance' },
  { slug: 'gym', label: 'Gym / Strength training' }, { slug: 'swimming', label: 'Swimming' },
  { slug: 'others', label: 'Others' }, { slug: 'none', label: 'No / very little workout' },
];
const WORKOUT_DURING_FALLBACK = [
  { slug: 'prenatal_yoga', label: 'Prenatal / Pregnancy Yoga' }, { slug: 'walking', label: 'Walking' },
  { slug: 'stretching', label: 'Light Stretching / Mobility' }, { slug: 'breathing', label: 'Breathing / Meditation' },
  { slug: 'swimming', label: 'Swimming' }, { slug: 'others', label: 'Others' },
  { slug: 'none', label: 'Nothing yet / very little' },
];
const LIFESTYLE_FALLBACK = [
  { slug: 'sedentary', label: 'Mostly sedentary' }, { slug: 'active', label: 'Fairly active' },
];
const GOALS_FALLBACK = [
  { slug: 'gain_strength', label: 'Gain Strength' }, { slug: 'mentally_calm', label: 'Mentally Calm' },
  { slug: 'more_active', label: 'More Active' }, { slug: 'normal_delivery', label: 'Normal Delivery' },
  { slug: 'better_sleep', label: 'Better Sleep' }, { slug: 'pain_relief', label: 'Pain Relief' },
];

// V67 (founder 9 Oct 2026) — 7 screens max, never more than two questions on one.
//   pregnancy = due date + scan · health = conditions + IVF · plan = next visit + review
const STEPS_BASE = ['pregnancy', 'health', 'before', 'during', 'goals', 'rhythm', 'plan'];
const STEPS_POSTNATAL = ['delivery', 'recovery', 'before', 'goals', 'rhythm', 'plan'];
const DAY_FALLBACK = [1, 2, 3, 4, 5, 6, 7].map((d) => ({ day: d, label: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d - 1] }));

// Client-side pregnancy-week estimate (mirrors backend week_from_edd).
const computeWeek = (eddStr) => {
  if (!eddStr) return null;
  const edd = new Date(eddStr);
  if (isNaN(edd.getTime())) return null;
  const days = 280 - Math.floor((edd.getTime() - Date.now()) / 86400000);
  if (days <= 0) return 1;
  return Math.max(1, Math.min(42, Math.floor(days / 7)));
};

export const CoachOnboarding = ({ config, edd, week, onDone, stage = 'prenatal',
                                  prefill = null, eddLocked = false, submitLabel }) => {
  const postnatal = stage === 'postnatal';
  const dayOpts = config?.practice_days || DAY_FALLBACK;
  const pnConditions = config?.postnatal_conditions || [];
  const pnGoals = config?.postnatal_goals || [];
  const gateWeek = config?.first_scan_gate_max_week ?? 12;
  const maxClasses = config?.max_videos_per_week ?? 5;
  const duringMinWeek = config?.workout_during_min_week ?? 15;
  const workoutBefore = config?.workout_before_options || WORKOUT_BEFORE_FALLBACK;
  const workoutDuring = config?.workout_during_options || WORKOUT_DURING_FALLBACK;
  const lifestyleOpts = config?.lifestyle_options || LIFESTYLE_FALLBACK;
  const goalOpts = config?.goals || GOALS_FALLBACK;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const p = prefill || {};
  // Anything she has answered before — in SoulAI or in the old booking check-in — is
  // pre-filled so she is never asked twice. She can still correct every one of them.
  const [answers, setAnswers] = useState({
    edd: p.edd || edd || '',
    first_scan_done: p.first_scan_done ?? null,
    conditions: p.conditions || [],
    none_selected: !!(p.conditions && p.conditions.length === 0 && p.source && p.source !== 'none'),
    ivf_iui: p.ivf_iui ?? null,
    classes_per_week: null,
    last_scan: p.last_scan_normal === false ? 'concern'
      : p.last_scan_recency === 'overdue' ? 'overdue'
        : p.last_scan_normal === true ? 'ok' : null,
    workout_before: p.workout_before || [], workout_during: p.workout_during || [],
    lifestyle_before: p.lifestyle_before || null, lifestyle_during: p.lifestyle_during || null,
    goals: p.goals || [],
    appt_mode: null, appt_type: 'scan', appt_date: '', appt_week: '',
    practice_days: p.practice_days || [], reminder_time: '07:00', reminder_on: true,
    delivery_type: p.delivery_type || null, delivery_date: p.delivery_date || '',
    doctor_clearance: p.doctor_clearance ?? null,
    postnatal_conditions: p.postnatal_conditions || [],
    pn_none: !!(p.postnatal_conditions && p.postnatal_conditions.length === 0 && p.source === 'soulai_onboarding'),
  });
  const conditions = config?.conditions || [];
  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const returning = p.source && p.source !== 'none';

  // Week-aware: past the dating-scan window the first-scan gate makes no sense,
  // so we ask about the MOST RECENT scan instead.
  const derivedWeek = computeWeek(answers.edd) ?? week ?? null;
  const isEarly = derivedWeek == null || derivedWeek <= gateWeek;
  // Only learners 15+ weeks answer the extra "during pregnancy" questions.
  const showDuring = derivedWeek != null && derivedWeek >= duringMinWeek;
  const STEPS = postnatal ? STEPS_POSTNATAL : STEPS_BASE.filter((x) => x !== 'during' || showDuring);
  const activeGoalOpts = postnatal && pnGoals.length ? pnGoals : goalOpts;
  const toggleDay = (d) => setAnswers((a) => {
    const has = a.practice_days.includes(d);
    if (!has && a.practice_days.length >= maxClasses) return a;
    return { ...a, practice_days: has ? a.practice_days.filter((x) => x !== d) : [...a.practice_days, d].sort((x, y) => x - y) };
  });
  const togglePnCondition = (slug) => setAnswers((a) => {
    const has = a.postnatal_conditions.includes(slug);
    return { ...a, pn_none: false, postnatal_conditions: has ? a.postnatal_conditions.filter((c) => c !== slug) : [...a.postnatal_conditions, slug] };
  });
  // Yoga level is derived: did yoga before pregnancy → experienced, else gentle start.
  const derivedLevel = (answers.workout_before || []).includes('yoga') ? 'intermediate' : 'beginner';

  const toggleCondition = (slug) => {
    setAnswers((a) => {
      const has = a.conditions.includes(slug);
      return { ...a, none_selected: false, conditions: has ? a.conditions.filter((c) => c !== slug) : [...a.conditions, slug] };
    });
  };

  // Multi-select toggle where `none` is mutually-exclusive with everything else.
  const toggleMulti = (key, slug, noneSlug = 'none') => {
    setAnswers((a) => {
      const arr = a[key] || [];
      if (slug === noneSlug) return { ...a, [key]: arr.includes(noneSlug) ? [] : [noneSlug] };
      const without = arr.filter((x) => x !== noneSlug);
      return { ...a, [key]: without.includes(slug) ? without.filter((x) => x !== slug) : [...without, slug] };
    });
  };

  const canNext = () => {
    const s = STEPS[step];
    if (s === 'pregnancy') {
      if (!answers.edd) return false;
      return isEarly ? answers.first_scan_done !== null : answers.last_scan !== null;
    }
    if (s === 'health') return (answers.none_selected || answers.conditions.length > 0) && answers.ivf_iui !== null;
    if (s === 'before') return answers.workout_before.length > 0 && !!answers.lifestyle_before;
    if (s === 'during') return answers.workout_during.length > 0 && !!answers.lifestyle_during;
    if (s === 'goals') return answers.goals.length > 0;
    if (s === 'rhythm') return answers.practice_days.length > 0;
    if (s === 'delivery') return !!answers.delivery_type && !!answers.delivery_date;
    if (s === 'recovery') return answers.doctor_clearance !== null && (answers.pn_none || answers.postnatal_conditions.length > 0);
    return true;
  };

  const canSubmit = () => {
    if (!answers.appt_mode) return false;
    if (answers.appt_mode === 'date') return !!answers.appt_date;
    if (answers.appt_mode === 'week') return !!answers.appt_week;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      // Later-week users: first (dating) scan is always done; capture last-scan signal.
      const firstScanDone = isEarly ? answers.first_scan_done : true;
      const payload = {
        conditions: answers.none_selected ? [] : answers.conditions,
        ivf_iui: !!answers.ivf_iui,
        fitness_level: derivedLevel,
        first_scan_done: postnatal ? true : firstScanDone,
        classes_per_week: answers.practice_days.length || answers.classes_per_week || undefined,
        practice_days: answers.practice_days,
        reminder_time: answers.reminder_on ? answers.reminder_time : null,
        workout_before: answers.workout_before,
        workout_during: showDuring && !postnatal ? answers.workout_during : [],
        lifestyle_before: answers.lifestyle_before,
        lifestyle_during: showDuring && !postnatal ? answers.lifestyle_during : null,
        goals: answers.goals,
        edd: answers.edd || undefined,
      };
      if (postnatal) {
        payload.stage = 'postnatal';
        payload.delivery_type = answers.delivery_type;
        payload.delivery_date = answers.delivery_date;
        payload.doctor_clearance = answers.doctor_clearance === true;
        payload.postnatal_conditions = answers.pn_none ? [] : answers.postnatal_conditions;
      }
      if (!postnatal && !isEarly && answers.last_scan) {
        payload.last_scan_recency = answers.last_scan === 'overdue' ? 'overdue' : 'recent';
        payload.last_scan_normal = answers.last_scan === 'concern' ? false
          : answers.last_scan === 'ok' ? true : null;
      }
      if (answers.appt_mode === 'date') {
        payload.next_appointment = { type: answers.appt_type, date: answers.appt_date, is_tentative: false };
      } else if (answers.appt_mode === 'week') {
        payload.next_appointment = { type: answers.appt_type, is_tentative: true, tentative_week: parseInt(answers.appt_week, 10) };
      }
      await api.post('/api/prenatal-coach/onboarding', payload);
      toast.success('All set! SoulAI is preparing your plan…');
      onDone?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'object' ? d?.message : d) || 'Failed to save — please try again');
    }
    setSaving(false);
  };

  const OptionBtn = ({ selected, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-colors duration-200 ${selected ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted text-foreground'}`}>
      {children}
    </button>
  );

  const Chip = ({ selected, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left px-3 py-2 rounded-xl border text-xs font-medium transition-colors duration-200 ${selected ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}>
      {children}
    </button>
  );

  const lifestyleLabel = (slug) => lifestyleOpts.find((o) => o.slug === slug)?.label || slug;
  const Head = ({ icon: Icon, children }) => (
    <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-primary" /><h2 className="font-heading text-base font-semibold">{children}</h2></div>
  );

  const s = STEPS[Math.min(step, STEPS.length - 1)];
  const last = s === 'plan';
  return (
    <div className="max-w-xl mx-auto space-y-4 pb-28 sm:pb-6" data-testid="coach-onboarding">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center"><Sparkles className="h-5 w-5 text-primary" /></div>
        <div>
          <h1 className="font-heading text-xl font-semibold">Let's set up your coach</h1>
          <p className="text-xs text-muted-foreground">Step {Math.min(step + 1, STEPS.length)} of {STEPS.length}</p>
        </div>
      </div>
      <Progress value={((Math.min(step, STEPS.length - 1) + 1) / STEPS.length) * 100} className="h-1.5" />

      <Card className="rounded-2xl">
        <CardContent className="pt-6 pb-6 space-y-4" data-testid={`onboarding-screen-${s}`}>
          {step === 0 && (
            <p className="text-sm text-muted-foreground leading-relaxed" data-testid="onboarding-intro">
              {postnatal
                ? "Congratulations, mama. I'm SoulAI — a few questions and I'll build you a gentle recovery plan matched to your delivery. Your doctor's clearance always comes first."
                : "Hi, I'm SoulAI — your prenatal coach. A few questions and I'll build your weekly plan of 15–20 minute classes, matched to your week and health profile. Your safety comes before everything else."}
              {returning && <span className="block mt-1 text-foreground">I've filled in what you've already told us — just check it and carry on.</span>}
            </p>
          )}

          {s === 'delivery' && (
            <div className="space-y-3">
              <Head icon={CalendarHeart}>About your delivery</Head>
              <p className="text-sm text-muted-foreground">This sets your recovery window — {config?.recovery_weeks?.vaginal ?? 6} weeks after a vaginal birth, {config?.recovery_weeks?.c_section ?? 8} after a C-section — before anything beyond breath and gentle movement.</p>
              <OptionBtn selected={answers.delivery_type === 'vaginal'} onClick={() => set('delivery_type', 'vaginal')} testid="delivery-type-vaginal">Vaginal birth</OptionBtn>
              <OptionBtn selected={answers.delivery_type === 'c_section'} onClick={() => set('delivery_type', 'c_section')} testid="delivery-type-c_section">C-section</OptionBtn>
              <p className="text-sm text-muted-foreground pt-1">When did you deliver?</p>
              <Input type="date" value={answers.delivery_date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => set('delivery_date', e.target.value)} data-testid="delivery-date-input" />
            </div>
          )}

          {/* Postnatal: doctor clearance + recovery notes on one screen */}
          {s === 'recovery' && (
            <div className="space-y-5">
              <div className="space-y-3">
                <Head icon={Stethoscope}>Has your doctor cleared you for exercise?</Head>
                <p className="text-sm text-muted-foreground">Usually at the 6-week postnatal check. Until then I keep everything to breath, pelvic floor and rest.</p>
                <OptionBtn selected={answers.doctor_clearance === true} onClick={() => set('doctor_clearance', true)} testid="clearance-yes">Yes — I've been cleared</OptionBtn>
                <OptionBtn selected={answers.doctor_clearance === false} onClick={() => set('doctor_clearance', false)} testid="clearance-no">Not yet</OptionBtn>
              </div>
              <div className="space-y-3 border-t pt-4">
                <Head icon={HeartPulse}>Anything I should know about your recovery?</Head>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {pnConditions.map((c) => (
                    <Chip key={c.slug} selected={answers.postnatal_conditions.includes(c.slug)}
                      onClick={() => togglePnCondition(c.slug)} testid={`pn-condition-${c.slug}`}>
                      {c.label}
                    </Chip>
                  ))}
                </div>
                <OptionBtn selected={answers.pn_none} testid="pn-condition-none"
                  onClick={() => setAnswers((a) => ({ ...a, pn_none: !a.pn_none, postnatal_conditions: [] }))}>
                  None of these — recovering well
                </OptionBtn>
              </div>
            </div>
          )}

          {/* Prenatal screen 1: due date + scan */}
          {s === 'pregnancy' && (
            <div className="space-y-5">
              <div className="space-y-3">
                <Head icon={CalendarHeart}>Your expected due date</Head>
                {edd && eddLocked ? (
                  <div className="rounded-xl border bg-muted/40 p-3" data-testid="onboarding-edd-locked">
                    <p className="text-sm font-medium flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-muted-foreground" />{formatDate(edd)}</p>
                    <p className="text-xs text-muted-foreground mt-1">This is the due date on your enrolment. If it needs to change, please reach out to our team and we'll update it for you.</p>
                  </div>
                ) : edd ? (
                  <p className="text-sm text-muted-foreground">From your profile: <span className="font-medium text-foreground">{formatDate(edd)}</span>. I'll plan around this.</p>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">Your gynaecologist gave you this date at your first scan — I need it to know your pregnancy week.</p>
                    <Input type="date" value={answers.edd} onChange={(e) => set('edd', e.target.value)} data-testid="onboarding-edd-input" />
                  </>
                )}
                {derivedWeek && <p className="text-xs text-emerald-700" data-testid="onboarding-derived-week">That puts you around <strong>week {derivedWeek}</strong>.</p>}
              </div>

              <div className="space-y-3 border-t pt-4">
                {isEarly ? (
                  <>
                    <Head icon={Stethoscope}>Have you had your first scan yet?</Head>
                    <p className="text-sm text-muted-foreground">Your dating scan confirms everything is on track — I wait for it before recommending movement.</p>
                    <OptionBtn selected={answers.first_scan_done === true} onClick={() => set('first_scan_done', true)} testid="onboarding-scan-yes">Yes, my first scan is done</OptionBtn>
                    <OptionBtn selected={answers.first_scan_done === false} onClick={() => set('first_scan_done', false)} testid="onboarding-scan-no">Not yet</OptionBtn>
                  </>
                ) : (
                  <>
                    <Head icon={Stethoscope}>How did your most recent scan or check-up go?</Head>
                    <p className="text-sm text-muted-foreground">I plan around your latest reports, so tell me how your last visit went.</p>
                    <OptionBtn selected={answers.last_scan === 'ok'} onClick={() => set('last_scan', 'ok')} testid="onboarding-lastscan-ok">
                      Recently — everything's on track <span className="block text-xs text-muted-foreground font-normal mt-0.5">All normal at my last scan/check-up</span>
                    </OptionBtn>
                    <OptionBtn selected={answers.last_scan === 'concern'} onClick={() => set('last_scan', 'concern')} testid="onboarding-lastscan-concern">
                      Recently — my doctor is watching something <span className="block text-xs text-muted-foreground font-normal mt-0.5">I'll keep your plan extra gentle to be safe</span>
                    </OptionBtn>
                    <OptionBtn selected={answers.last_scan === 'overdue'} onClick={() => set('last_scan', 'overdue')} testid="onboarding-lastscan-overdue">
                      It's been over 2 months <span className="block text-xs text-muted-foreground font-normal mt-0.5">I'll gently remind you to book your next visit</span>
                    </OptionBtn>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Prenatal screen 2: conditions + IVF */}
          {s === 'health' && (
            <div className="space-y-5">
              <div className="space-y-3">
                <Head icon={HeartPulse}>Has your doctor mentioned any of these?</Head>
                <p className="text-sm text-muted-foreground">Select all that apply — this is how I keep every class safe for you, and how we match you to the right live batch.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {conditions.map((c) => (
                    <Chip key={c.slug} selected={answers.conditions.includes(c.slug)}
                      onClick={() => toggleCondition(c.slug)} testid={`condition-${c.slug}`}>
                      {c.label}
                    </Chip>
                  ))}
                </div>
                <OptionBtn selected={answers.none_selected} testid="condition-none"
                  onClick={() => setAnswers((a) => ({ ...a, none_selected: !a.none_selected, conditions: [] }))}>
                  None of these — all clear so far
                </OptionBtn>
              </div>
              <div className="space-y-3 border-t pt-4">
                <Head icon={Sparkles}>Did you conceive via IVF or IUI?</Head>
                <p className="text-sm text-muted-foreground">IVF/IUI pregnancies get extra-gentle care in the first half — after week 20 with normal reports, we treat it like any other pregnancy.</p>
                <OptionBtn selected={answers.ivf_iui === true} onClick={() => set('ivf_iui', true)} testid="onboarding-ivf-yes">Yes — IVF / IUI</OptionBtn>
                <OptionBtn selected={answers.ivf_iui === false} onClick={() => set('ivf_iui', false)} testid="onboarding-ivf-no">No — natural conception</OptionBtn>
              </div>
            </div>
          )}

          {s === 'before' && (
            <div className="space-y-4">
              <Head icon={Dumbbell}>{postnatal ? 'Before your baby arrived' : 'Before pregnancy'}</Head>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Which of these did you do <span className="font-medium text-foreground">before</span> pregnancy? Pick all that apply.</p>
                <div className="grid grid-cols-2 gap-2">
                  {workoutBefore.map((o) => (
                    <Chip key={o.slug} selected={(answers.workout_before || []).includes(o.slug)}
                      onClick={() => toggleMulti('workout_before', o.slug)} testid={`workout-before-${o.slug}`}>
                      {o.label}
                    </Chip>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">How would you describe your lifestyle before pregnancy?</p>
                <div className="grid grid-cols-2 gap-2">
                  {lifestyleOpts.map((o) => (
                    <OptionBtn key={o.slug} selected={answers.lifestyle_before === o.slug}
                      onClick={() => set('lifestyle_before', o.slug)} testid={`lifestyle-before-${o.slug}`}>
                      {o.label}
                    </OptionBtn>
                  ))}
                </div>
              </div>
            </div>
          )}

          {s === 'during' && (
            <div className="space-y-4">
              <Head icon={Activity}>During pregnancy so far</Head>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">You're around week {derivedWeek}. Are you doing any of these <span className="font-medium text-foreground">during</span> pregnancy? Pick all that apply.</p>
                <div className="grid grid-cols-2 gap-2">
                  {workoutDuring.map((o) => (
                    <Chip key={o.slug} selected={(answers.workout_during || []).includes(o.slug)}
                      onClick={() => toggleMulti('workout_during', o.slug)} testid={`workout-during-${o.slug}`}>
                      {o.label}
                    </Chip>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">And your lifestyle during pregnancy?</p>
                <div className="grid grid-cols-2 gap-2">
                  {lifestyleOpts.map((o) => (
                    <OptionBtn key={o.slug} selected={answers.lifestyle_during === o.slug}
                      onClick={() => set('lifestyle_during', o.slug)} testid={`lifestyle-during-${o.slug}`}>
                      {o.label}
                    </OptionBtn>
                  ))}
                </div>
              </div>
            </div>
          )}

          {s === 'goals' && (
            <div className="space-y-3">
              <Head icon={Target}>What matters most to you?</Head>
              <p className="text-sm text-muted-foreground">Pick the goals you care about — I'll keep them in mind (your safety and pregnancy stage always come first).</p>
              <div className="grid grid-cols-2 gap-2">
                {activeGoalOpts.map((o) => (
                  <Chip key={o.slug} selected={(answers.goals || []).includes(o.slug)}
                    onClick={() => toggleMulti('goals', o.slug)} testid={`goal-${o.slug}`}>
                    {o.label}
                  </Chip>
                ))}
              </div>
            </div>
          )}

          {s === 'rhythm' && (
            <div className="space-y-3">
              <Head icon={ListChecks}>Which days would you like to practise?</Head>
              <p className="text-sm text-muted-foreground">Pick up to {maxClasses} days — one 15–20 minute class per day. I'll place stronger classes on non-consecutive days.</p>
              <div className="grid grid-cols-7 gap-1.5" data-testid="practice-days-grid">
                {dayOpts.map((d) => (
                  <button key={d.day} type="button" onClick={() => toggleDay(d.day)} data-testid={`practice-day-${d.day}`}
                    className={`py-2.5 rounded-xl border text-xs font-semibold transition-colors duration-200 ${answers.practice_days.includes(d.day) ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted'}`}>
                    {d.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground" data-testid="practice-days-count">
                {answers.practice_days.length === 0 ? 'Choose at least one day' : `${answers.practice_days.length} class${answers.practice_days.length > 1 ? 'es' : ''} a week${answers.practice_days.length === 3 ? ' — a steady, balanced rhythm' : ''}`}
              </p>
              <div className="rounded-xl border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Daily reminder</p>
                  <button type="button" onClick={() => set('reminder_on', !answers.reminder_on)} data-testid="reminder-toggle"
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${answers.reminder_on ? 'bg-primary/10 border-primary text-primary' : 'border-border'}`}>
                    {answers.reminder_on ? 'On' : 'Off'}
                  </button>
                </div>
                {answers.reminder_on && (
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-muted-foreground">Nudge me at</p>
                    <Input type="time" value={answers.reminder_time} onChange={(e) => set('reminder_time', e.target.value)} className="w-32 h-8 text-sm" data-testid="reminder-time-input" />
                    <p className="text-xs text-muted-foreground">on my practice days</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Last screen: next visit + the full summary, right above the submit button */}
          {s === 'plan' && (
            <div className="space-y-5">
              <div className="space-y-3">
                <Head icon={CalendarClock}>When's your next scan or doctor visit?</Head>
                <p className="text-sm text-muted-foreground">I'll check in after it so your plan always matches your latest reports. A rough week works too!</p>
                <div className="flex gap-2 mb-1">
                  <button type="button" onClick={() => set('appt_type', 'scan')} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${answers.appt_type === 'scan' ? 'bg-primary/10 border-primary text-primary' : 'border-border'}`} data-testid="appt-type-scan">Scan</button>
                  <button type="button" onClick={() => set('appt_type', 'doctor_visit')} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${answers.appt_type === 'doctor_visit' ? 'bg-primary/10 border-primary text-primary' : 'border-border'}`} data-testid="appt-type-visit">Doctor visit</button>
                </div>
                <OptionBtn selected={answers.appt_mode === 'date'} onClick={() => set('appt_mode', 'date')} testid="appt-mode-date">I know the exact date</OptionBtn>
                {answers.appt_mode === 'date' && <Input type="date" value={answers.appt_date} onChange={(e) => set('appt_date', e.target.value)} data-testid="appt-date-input" />}
                <OptionBtn selected={answers.appt_mode === 'week'} onClick={() => set('appt_mode', 'week')} testid="appt-mode-week">I know it roughly (around a week)</OptionBtn>
                {answers.appt_mode === 'week' && <Input type="number" min="4" max="42" placeholder="Around pregnancy week… e.g. 20" value={answers.appt_week} onChange={(e) => set('appt_week', e.target.value)} data-testid="appt-week-input" />}
                <OptionBtn selected={answers.appt_mode === 'skip'} onClick={() => set('appt_mode', 'skip')} testid="appt-mode-skip">Not sure yet — I'll add it later</OptionBtn>
              </div>

              <div className="space-y-3 border-t pt-4" data-testid="onboarding-review">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /><h2 className="font-heading text-base font-semibold">Everything look right?</h2></div>
                <div className="space-y-2 text-sm">
                  {postnatal ? (
                    <>
                      <p><span className="text-muted-foreground">Delivery:</span> {answers.delivery_type === 'c_section' ? 'C-section' : 'Vaginal'} on {formatDate(answers.delivery_date)}</p>
                      <p><span className="text-muted-foreground">Doctor clearance:</span> {answers.doctor_clearance ? 'Yes' : 'Not yet — gentle recovery plan'}</p>
                      <div className="flex flex-wrap gap-1 items-center"><span className="text-muted-foreground">Recovery notes:</span>
                        {answers.pn_none || answers.postnatal_conditions.length === 0 ? ' None' :
                          answers.postnatal_conditions.map((slug) => <Badge key={slug} variant="outline" className="text-[10px]">{pnConditions.find((c) => c.slug === slug)?.label || slug}</Badge>)}
                      </div>
                    </>
                  ) : (
                    <>
                      <p><span className="text-muted-foreground">Due date:</span> {formatDate(answers.edd || edd)}</p>
                      {isEarly ? (
                        <p><span className="text-muted-foreground">First scan:</span> {answers.first_scan_done ? 'Done' : 'Not yet'}</p>
                      ) : (
                        <p><span className="text-muted-foreground">Recent scan:</span> {answers.last_scan === 'ok' ? 'All normal' : answers.last_scan === 'concern' ? 'Doctor watching something' : answers.last_scan === 'overdue' ? 'Over 2 months ago' : '—'}</p>
                      )}
                      <div className="flex flex-wrap gap-1 items-center"><span className="text-muted-foreground">Conditions:</span>
                        {answers.none_selected || answers.conditions.length === 0 ? ' None' :
                          answers.conditions.map((slug) => <Badge key={slug} variant="outline" className="text-[10px]">{conditions.find((c) => c.slug === slug)?.label || slug}</Badge>)}
                      </div>
                      <p><span className="text-muted-foreground">IVF / IUI:</span> {answers.ivf_iui ? 'Yes' : 'No'}</p>
                    </>
                  )}
                  <p data-testid="review-practice-days"><span className="text-muted-foreground">Practice days:</span> {answers.practice_days.length ? answers.practice_days.map((d) => (dayOpts.find((o) => o.day === d)?.label || d)).join(', ') : '—'}{answers.reminder_on ? ` · reminder ${answers.reminder_time}` : ''}</p>
                  <div className="flex flex-wrap gap-1 items-center"><span className="text-muted-foreground">Before pregnancy:</span>
                    {(answers.workout_before || []).map((slug) => <Badge key={slug} variant="outline" className="text-[10px]">{workoutBefore.find((o) => o.slug === slug)?.label || slug}</Badge>)}
                    {answers.lifestyle_before && <span className="text-xs text-muted-foreground">· {lifestyleLabel(answers.lifestyle_before)}</span>}
                  </div>
                  {showDuring && !postnatal && (
                    <div className="flex flex-wrap gap-1 items-center"><span className="text-muted-foreground">During pregnancy:</span>
                      {(answers.workout_during || []).map((slug) => <Badge key={slug} variant="outline" className="text-[10px]">{workoutDuring.find((o) => o.slug === slug)?.label || slug}</Badge>)}
                      {answers.lifestyle_during && <span className="text-xs text-muted-foreground">· {lifestyleLabel(answers.lifestyle_during)}</span>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 items-center"><span className="text-muted-foreground">Goals:</span>
                    {(answers.goals || []).length === 0 ? ' —' :
                      answers.goals.map((slug) => <Badge key={slug} variant="outline" className="text-[10px]">{activeGoalOpts.find((o) => o.slug === slug)?.label || slug}</Badge>)}
                  </div>
                  <p><span className="text-muted-foreground">Yoga level:</span> {derivedLevel === 'beginner' ? 'New / gentle start' : 'Experienced'}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={() => setStep((v) => Math.max(0, v - 1))} disabled={step === 0 || saving} data-testid="onboarding-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" />Back
        </Button>
        {last ? (
          <Button onClick={submit} disabled={saving || !canSubmit()} data-testid="onboarding-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}{submitLabel || 'Build my plan'}
          </Button>
        ) : (
          <Button onClick={() => setStep((v) => v + 1)} disabled={!canNext()} data-testid="onboarding-next-btn">
            Next<ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
};
